import React, { useState, useEffect } from "react";
import { Table, Row, Col, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import programsGrantsIcon from "../../../assets/images/programs_grants_icon.png";
import FormModal from "../../../components/FormModal";
import { postData } from "../../../api";
import { autocloseTiming } from "../../../api/regex";

const EMPTY_PROGRAM = {
  program_name: "",
  location_name: "",
  grant_name: "",
};

const PROGRAM_FIELDS = [
  { name: "program_name", label: "Program Name", error: "program name" },
  { name: "location_name", label: "Location", error: "location" },
  { name: "grant_name", label: "Grant Name", error: "grant name" },
];

const sortPrograms = (programs) =>
  Array.isArray(programs)
    ? [...programs].sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
    : [];

const ProgramsTab = ({
  programsData,
  jobLocations,
  clientRole,
  groupId,
  userid,
}) => {
  const [programsList, setProgramsList] = useState(() =>
    sortPrograms(programsData)
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [addProgramData, setAddProgramData] = useState(EMPTY_PROGRAM);
  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProgramsList(sortPrograms(programsData));
  }, [programsData]);

  // Older programs store a job_location_id instead of location_name.
  const getLocationName = (program) => {
    if (program?.location_name) return program.location_name;
    const jobLocation = Array.isArray(jobLocations)
      ? jobLocations.find((location) => location?.id === program?.job_location_id)
      : null;
    return jobLocation?.title || "-";
  };

  const canAddProgram = false;

  const handleAddOpen = () => {
    setAddProgramData(EMPTY_PROGRAM);
    setErrors({});
    setValidated(false);
    setShowAddModal(true);
  };

  const handleAddClose = () => {
    setShowAddModal(false);
    setErrors({});
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setAddProgramData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const findFormErrors = () => {
    const newErrors = {};
    PROGRAM_FIELDS.forEach(({ name, error }) => {
      if (!addProgramData[name]?.trim()) {
        newErrors[name] = `Please enter ${error}.`;
      }
    });
    return newErrors;
  };

  const addProgram = async () => {
    setSaving(true);
    const data = {
      user_id: userid,
      stage: "programs",
      type: "create",
      program_name: addProgramData.program_name.trim(),
      location_name: addProgramData.location_name.trim(),
      grant_name: addProgramData.grant_name.trim(),
    };
    const res = await postData("profile", {}, data);
    if (res?.status === true) {
      if (Array.isArray(res?.data?.programs)) {
        setProgramsList(sortPrograms(res.data.programs));
      }
      setShowAddModal(false);
      setValidated(false);
      toast.success(res.message, {
        theme: "colored",
        autoClose: autocloseTiming,
      });
    } else {
      toast.error(res?.message, {
        theme: "colored",
        autoClose: autocloseTiming,
      });
    }
    setSaving(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = findFormErrors();
    setValidated(true);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      addProgram();
    }
  };

  return (
    <div className="programs_container">
      <Row className="mb-3 align-items-center">
        <Col>
          <h5 className="tabs_main_heading mb-0">
            <span className="me-1">
              <img src={programsGrantsIcon} alt="" />
            </span>
            Programs / Grants
          </h5>
        </Col>
        {canAddProgram && (
          <Col xs="auto">
            <Button
              variant="primary"
              className="search_btn_profile btn_clr_white"
              onClick={handleAddOpen}
            >
              Add Program
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col xs={12} className="table_mobile_scroll">
          <Table responsive className="mt-4 header_bg_table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Location</th>
                <th>Grant</th>
              </tr>
            </thead>
            <tbody>
              {programsList.length > 0 ? (
                programsList.map((program) => (
                  <tr key={program?.id}>
                    <td>{program?.program_name || "-"}</td>
                    <td>{getLocationName(program)}</td>
                    <td>{program?.grant_name || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">
                    <h4 className="no-data mb-0">No data found</h4>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
      <FormModal
        heading="Add Program"
        show={showAddModal}
        size="lg"
        onClose={handleAddClose}
        modalClassName="view_task_modal"
      >
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          autoComplete="off"
          className="mt-3"
        >
          <Row>
            {PROGRAM_FIELDS.map(({ name, label }) => (
              <Col md={12} key={name}>
                <Form.Group className="mb-3" controlId={`add_${name}`}>
                  <Form.Label>
                    {label} <span className="asterisk">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name={name}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    value={addProgramData[name]}
                    onChange={handleInput}
                    isInvalid={!!errors[name]}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[name]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            ))}
          </Row>
          <div className="text-end">
            <Button
              type="button"
              variant="secondary"
              className="me-3 cancel_btn btn-default"
              onClick={handleAddClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn_clr_white save_btn"
              type="submit"
              disabled={saving}
            >
              Save
            </Button>
          </div>
        </Form>
      </FormModal>
    </div>
  );
};

export default ProgramsTab;
